import { useRef, useState } from 'react';
import { useCuttingStore } from '../state/useCuttingStore';
import { ProcessingDict, type ProcessingEntry } from '../lib/processingDict';

/**
 * Block 14 «Раскрой» — селектор обработки детали (PR #10b-2, CUT-3.3).
 *
 * Пиксельный порт `project.html` (#process-modal) + логики `app.js:397-447`
 * (openProcessDialog / renderProcessOptions / applyProcessValue / addCustomProcessing).
 * В отличие от Edge/GrooveModal (сетка 4 сторон) — здесь вертикальный СПИСОК-словарь:
 * «Без обработки» + записи `ProcessingDict` (частые сверху). Клик по опции
 * ПРИМЕНЯЕТ СРАЗУ (`updatePart(id,{processing})` + учёт в словаре) → модалка без «ОК»,
 * footer только «Отмена». Custom-row (`input` + «+ Добавить») дописывает свой вид.
 *
 * Обработка — feature-local (как пазы, см. PartSchema.processing): IDB-local персист,
 * в замороженный контракт не идёт; legacy `do` выводится из неё (toContractPart).
 * Шелл `.sc-modal2-overlay` с × в шапке — прецедент Edge/GrooveModal (#10a/#10b-1).
 *
 * @see Помощник — Прототип Раскроя/01-prototype-vanilla/js/app.js:397-447
 */

/** Иконка записи (вербатим app.js:415,419): дефолты C/F, пользовательские ★. */
function entryIcon(e: ProcessingEntry): string {
  if (!e.isDefault) return '★';
  return e.name === 'Склейка' ? 'C' : 'F';
}

/** Бейдж записи (вербатим app.js:420): N× при использовании / «мой» для своих. */
function entryBadge(e: ProcessingEntry): string | null {
  if (e.useCount > 0) return `${e.useCount}×`;
  return e.isDefault ? null : 'мой';
}

export function ProcessModal({
  partId,
  partLabel,
  onClose,
}: {
  partId: string;
  partLabel: number;
  onClose: () => void;
}) {
  const updatePart = useCuttingStore((s) => s.updatePart);
  // Текущее значение обработки детали и снимок словаря снимаем один раз (модалка —
  // транзиентный шаг; useCount меняется только при применении и сразу закрывает).
  const [current] = useState<string>(() => {
    const part = useCuttingStore.getState().document.parts.find((p) => p.id === partId);
    return part?.processing ?? '';
  });
  const [entries] = useState<ProcessingEntry[]>(() => ProcessingDict.getAll());
  const [customValue, setCustomValue] = useState('');
  const customRef = useRef<HTMLInputElement>(null);

  /** Применить значение обработки: записать в стор, учесть в словаре, закрыть. */
  const apply = (value: string) => {
    updatePart(partId, { processing: value });
    if (value) ProcessingDict.add(value); // «без обработки» ('') в словарь не пишем
    onClose();
  };

  /** «+ Добавить» свой вид: пусто → фокус, иначе добавить в словарь и применить. */
  const addCustom = () => {
    const v = customValue.trim();
    if (!v) {
      customRef.current?.focus();
      return;
    }
    apply(v); // apply сам делает ProcessingDict.add(v)
  };

  return (
    <div
      className="sc-modal2-overlay visible"
      role="dialog"
      aria-modal="true"
      aria-label={`Обработка детали ${partLabel}`}
    >
      <div className="sc-modal2">
        <div className="sc-modal2-title">
          <span>Обработка</span>
          <button type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="sc-modal2-content">
          <div className="process-options-list">
            <button
              type="button"
              className={`process-option${current === '' ? ' selected' : ''}`}
              aria-pressed={current === ''}
              onClick={() => apply('')}
            >
              <span className="ico">∅</span>
              <span className="title">Без обработки</span>
            </button>
            {entries.map((e) => {
              const badge = entryBadge(e);
              return (
                <button
                  key={e.name}
                  type="button"
                  className={`process-option${e.isDefault ? '' : ' user'}${
                    current === e.name ? ' selected' : ''
                  }`}
                  aria-pressed={current === e.name}
                  onClick={() => apply(e.name)}
                >
                  <span className="ico">{entryIcon(e)}</span>
                  <span className="title">{e.name}</span>
                  {badge && <span className="badge">{badge}</span>}
                </button>
              );
            })}
          </div>
          <div className="process-custom-row">
            <input
              ref={customRef}
              type="text"
              className="sc-input"
              aria-label="Свой вид обработки"
              placeholder="Свой вариант (Шлифовка, Сверление…)"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addCustom();
              }}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={addCustom}>
              + Добавить
            </button>
          </div>
        </div>
        <div className="sc-modal2-footer">
          <button type="button" className="sc-modal-btn" onClick={onClose}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
